import { Component, For, Show, createSignal, onCleanup } from "solid-js";

/**
 * Character info for machine switcher
 */
export interface MachineInfo {
  /** Character's machine name (e.g., "chen-dev-station") */
  machineName: string;
  /** Character's display name (e.g., "Dr. Sarah Chen") */
  displayName: string;
  /** Character's clearance level (e.g., "L5") */
  clearanceLevel: string;
  /** Character's status (e.g., "MISSING", "ACTIVE", "TERMINATED") */
  status: string;
  /** Route path for this character's desktop (e.g., "/games/desktop/chen") */
  route: string;
}

/**
 * Props for the MachineSwitcher component
 */
export interface MachineSwitcherProps {
  /** Current machine name to display and highlight */
  currentMachine: string;
  /** List of available machines */
  machines: MachineInfo[];
  /** Callback when a machine is selected */
  onMachineSelect: (route: string) => void;
}

/**
 * Get status color classes based on status
 */
function getStatusColor(status: string): {
  bg: string;
  text: string;
  border: string;
} {
  switch (status) {
    case "MISSING":
      return {
        bg: "bg-red-500/20",
        text: "text-red-400",
        border: "border-red-500/30",
      };
    case "ACTIVE":
      return {
        bg: "bg-green-500/20",
        text: "text-green-400",
        border: "border-green-500/30",
      };
    case "TERMINATED":
      return {
        bg: "bg-gray-500/20",
        text: "text-gray-400",
        border: "border-gray-500/30",
      };
    default:
      return {
        bg: "bg-amber-500/20",
        text: "text-amber-400",
        border: "border-amber-500/30",
      };
  }
}

/**
 * MachineSwitcher - Dropdown for switching between character desktops
 *
 * A clickable machine name that opens a dropdown showing all available
 * character workstations with their status and clearance levels.
 *
 * @example
 * <MachineSwitcher
 *   currentMachine="chen-dev-station"
 *   machines={[...]}
 *   onMachineSelect={(route) => navigate(route)}
 * />
 */
export const MachineSwitcher: Component<MachineSwitcherProps> = (props) => {
  const [isOpen, setIsOpen] = createSignal(false);
  let dropdownRef: HTMLDivElement | undefined;

  const handleMachineClick = (route: string) => {
    setIsOpen(false);
    props.onMachineSelect(route);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (
      dropdownRef &&
      e.target instanceof Node &&
      !dropdownRef.contains(e.target)
    ) {
      setIsOpen(false);
    }
  };

  // Add click outside listener
  const handleToggle = () => {
    const newState = !isOpen();
    setIsOpen(newState);

    if (newState) {
      // Add listener on next tick to avoid immediate close
      setTimeout(() => {
        document.addEventListener("click", handleClickOutside);
      }, 0);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }
  };

  // Cleanup listener on component unmount
  onCleanup(() => {
    document.removeEventListener("click", handleClickOutside);
  });

  return (
    <div class="relative" ref={dropdownRef}>
      {/* Current Machine Button */}
      <button
        onClick={handleToggle}
        class="text-whitesmoke/40 hover:text-amber flex max-w-[180px] items-center gap-2 font-mono text-sm transition-colors sm:max-w-none"
        title="Switch workstation"
      >
        <span class="text-amber flex-shrink-0 animate-pulse">◇</span>
        <span class="min-w-0 truncate">{props.currentMachine}</span>
        <svg
          class="h-3 w-3 flex-shrink-0 transition-transform"
          classList={{ "rotate-180": isOpen() }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      <Show when={isOpen()}>
        <div class="absolute top-full left-0 z-50 mt-2 min-w-[320px]">
          <div
            class="border-amber/20 bg-charcoal/95 rounded-lg border shadow-2xl backdrop-blur-sm"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div class="border-amber/20 border-b px-4 py-3">
              <div class="text-whitesmoke/60 font-mono text-xs tracking-wider uppercase">
                Available Workstations
              </div>
            </div>

            {/* Machine List */}
            <div class="max-h-[400px] overflow-y-auto p-2">
              <For each={props.machines}>
                {(machine) => {
                  const colors = getStatusColor(machine.status);
                  const isCurrent =
                    machine.machineName === props.currentMachine;

                  return (
                    <button
                      onClick={() => handleMachineClick(machine.route)}
                      class={`mb-1 w-full rounded-md border-2 px-3 py-2 text-left transition-all ${
                        isCurrent
                          ? `${colors.border} bg-amber/10`
                          : "bg-whitesmoke/5 hover:bg-whitesmoke/10 border-transparent"
                      }`}
                      classList={{
                        "opacity-60": machine.status === "TERMINATED",
                      }}
                    >
                      {/* Machine Name */}
                      <div class="mb-1 flex items-center justify-between">
                        <div class="text-whitesmoke/80 font-mono text-sm">
                          {machine.machineName}
                        </div>
                        <div
                          class={`rounded-full ${colors.bg} px-2 py-0.5 text-[10px] font-bold ${colors.text}`}
                        >
                          {machine.status}
                        </div>
                      </div>

                      {/* Character Info */}
                      <div class="text-whitesmoke/50 text-xs">
                        <div>{machine.displayName}</div>
                        <div class="text-[10px]">
                          {machine.clearanceLevel} Clearance
                        </div>
                      </div>

                      {/* Current Indicator */}
                      <Show when={isCurrent}>
                        <div class="text-amber mt-1 text-[10px] font-bold">
                          ▸ CURRENT SESSION
                        </div>
                      </Show>
                    </button>
                  );
                }}
              </For>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
};
